<script setup>
import {computed, ref} from "vue";
import {useApartmentStore} from "@/stores/apartment";
import AppIcon from "@/components/AppIcon.vue";

const panels = [
  {type: "accommodation", icon: "home"},
  {type: "arrival", icon: "location_on"},
  {type: "checkin", icon: "event_available"},
  {type: "checkout", icon: "event_busy"},
  {type: "internet_and_tv", icon: "wifi"},
  {type: "trash", icon: "delete"},
  {type: "activities", icon: "map"},
  {type: "emergency", icon: "emergency"},
  {type: "house_rules", icon: "balance"},
]

const apartmentStore = useApartmentStore()
// `undefined` means the guest has not picked a chapter yet, `null` means all closed.
const openType = ref(undefined)

const guides = computed(() => {
  return panels
      .map(row => {
        const {content} = apartmentStore.apartment.guides?.[row.type] ?? {}

        return {...row, content: content ?? null}
      })
      .filter(row => row.content !== null);
})

// Open the first chapter by default so the screen never starts empty.
const openedType = computed(() => openType.value === undefined ? guides.value[0]?.type : openType.value)

function toggle(type) {
  openType.value = openedType.value === type ? null : type
}

function body(guide, locale, fallbackLocale) {
  return guide.content[locale] ?? guide.content[fallbackLocale] ?? ''
}
</script>

<template>
  <div class="ww-screen">
    <h1 class="ww-title">{{ $t('guestfolder') }}</h1>
    <p class="ww-subtitle">{{ $t('guestfolderSubtitle') }}</p>

    <div class="folder">
      <div v-for="guide in guides" :key="guide.type" class="folder__item">
        <button
            type="button"
            class="folder__head"
            :class="{'folder__head--open': openedType === guide.type}"
            :aria-expanded="openedType === guide.type"
            :aria-controls="'guide-' + guide.type"
            @click="toggle(guide.type)"
        >
          <app-icon :name="guide.icon" :size="22" class="folder__icon"/>
          <span class="folder__label">{{ $t('guestFolderType.' + guide.type) }}</span>
          <app-icon
              :name="openedType === guide.type ? 'expand_less' : 'chevron_right'"
              :size="20"
              class="folder__chevron"
          />
        </button>

        <div
            v-if="openedType === guide.type"
            :id="'guide-' + guide.type"
            class="folder__body ww-animate-grow"
        >
          <vue-markdown
              class="ww-prose"
              :source="body(guide, $i18n.locale, $i18n.fallbackLocale)"
              :options="{html: true}"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.folder {
  margin-top: 20px;
  background: var(--ww-surface);
  border: 1px solid var(--ww-border);
  border-radius: var(--ww-r-xl);
  overflow: hidden;
}

.folder__item + .folder__item {
  border-top: 1px solid var(--ww-border-line);
}

.folder__head {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 17px 18px;
  border: 0;
  background: var(--ww-surface);
  text-align: left;
  transition: background-color 0.2s ease;
}

/* Drawn inwards: the row spans the full width of a card that clips overflow. */
.folder__head:focus-visible {
  outline-offset: -3px;
}

.folder__head--open {
  background: var(--ww-surface-hover);
}

.folder__icon {
  color: var(--ww-text-muted);
}

.folder__head--open .folder__icon {
  color: var(--ww-primary);
}

.folder__label {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 16px;
  font-weight: 700;
  color: var(--ww-text);
}

.folder__head--open .folder__label {
  color: var(--ww-primary-dark);
}

.folder__chevron {
  color: var(--ww-icon-faint);
}

.folder__head--open .folder__chevron {
  color: var(--ww-primary);
}

.folder__body {
  padding: 4px 18px 22px;
}
</style>
